'use client';

import { c as _c } from "react/compiler-runtime";
import type { Data } from '@repo/strapi-types';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import AppLink from '@/components/elementary/AppLink';
import { ArrowRight } from '@/components/icons/ArrowRight';
import { formatEventName, useAnalytics } from '@/lib/analytics/analytics';
import { ANALYTICS_EVENTS } from '@/lib/analytics/analytics.constants';
import { useComponentTracking } from '@/lib/analytics/component-tracking';
import { cn } from '@/lib/styles';

// Sole localStorage key: holds the dismissed announcement's `publishedAt`, so an edit re-shows it.
const DISMISS_STORAGE_KEY = 'lok:announcement-dismissed';

// Survives client-side navigations such as locale switches, so a remounted banner that is already showing skips the enter animation.
let visibleSignature: string | null = null;

// Keeps a dismissal for the rest of the session when localStorage is unavailable.
let dismissedSignature: string | null = null;
function isDismissedInStorage(signature: string) {
  try {
    return window.localStorage.getItem(DISMISS_STORAGE_KEY) === signature;
  } catch {
    return false;
  }
}
interface AnnouncementBannerProps {
  text: string;
  link?: Data.Component<'utilities.link'> | null;
  /** Signature of the current content; written to localStorage on dismiss. */
  signature: string;
}
export function AnnouncementBanner(t0) {
  const $ = _c(24);
  const {
    text,
    link,
    signature
  } = t0;
  let t1;
  if ($[0] !== signature) {
    t1 = () => visibleSignature === signature;
    $[0] = signature;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [wasAlreadyVisible] = useState(t1);
  const [isDismissed, setIsDismissed] = useState(!wasAlreadyVisible);
  const {
    track
  } = useAnalytics();
  const componentTracking = useComponentTracking();
  let t2;
  let t3;
  if ($[2] !== signature) {
    t2 = () => {
      const dismissed = dismissedSignature === signature || isDismissedInStorage(signature);
      setIsDismissed(dismissed);
      visibleSignature = dismissed ? null : signature;
    };
    t3 = [signature];
    $[2] = signature;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== componentTracking || $[6] !== signature || $[7] !== track) {
    t4 = () => {
      track({
        eventType: formatEventName(ANALYTICS_EVENTS.ANNOUNCEMENT_DISMISSED),
        eventProperties: {
          ...(componentTracking && {
            component: componentTracking.component
          })
        }
      });
      try {
        window.localStorage.setItem(DISMISS_STORAGE_KEY, signature);
      } catch {}
      setIsDismissed(true);
      visibleSignature = null;
      dismissedSignature = signature;
    };
    $[5] = componentTracking;
    $[6] = signature;
    $[7] = track;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const dismiss = t4;
  if (isDismissed) {
    return null;
  }
  const t5 = wasAlreadyVisible || undefined;
  let t6;
  if ($[9] !== link) {
    t6 = link?.href ? <AppLink link={link} variant="none" iconEnd={<ArrowRight className="size-4 shrink-0" />} className="lok-text-body-bold-small inline-flex items-center gap-1 align-middle text-wolf-gray-600 underline" /> : null;
    $[9] = link;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6 || $[12] !== text) {
    t7 = <div className="lok-text-body-regular-small text-wolf-gray-900">{text}{" "}{t6}</div>;
    $[11] = t6;
    $[12] = text;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer", "flex size-4 items-center justify-center text-wolf-gray-900", "transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wolf-gray-600");
    t9 = <X className="size-4" />;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  let t10;
  if ($[16] !== dismiss) {
    t10 = <button type="button" onClick={dismiss} aria-label="Dismiss announcement" className={t8}>{t9}</button>;
    $[16] = dismiss;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t7) {
    t11 = <div className="relative w-full px-11 text-center">{t7}{t10}</div>;
    $[18] = t10;
    $[19] = t7;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t5) {
    t12 = <div data-announcement-bar={true} data-skip-enter={t5} className="gradient-announcement-bar fixed inset-x-0 top-0 z-50 flex h-14 w-full items-center overflow-hidden lg:h-9">{t11}</div>;
    $[21] = t11;
    $[22] = t5;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}